import { Box, Button, CircularProgress, Stack, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import * as TinyMCETypes from "tinymce";
import LoadingSpinner from "../../../App/Components/LoadingSpinner/LoadingSpinner";
import Modal from "../../../App/Components/Modal/Modal";
import ScheduleEmail from "../../../MessageComponents/MessageDialog/Components/ScheduleEmail";
import SendEmailNow from "../../../MessageComponents/MessageDialog/Components/SendEmailNow";
import EmailMessageInput from "../../../MessageComponents/SendGroupMessage/Components/SharedComponents/EmailMessageInput";
import CandidatesStep from "../../Components/Steps/CandidatesStep";
import MessageCampaignInfoStep from "../../Components/Steps/MessageCampaignInfoStep";
import ReviewStep from "../../Components/Steps/ReviewStep";
import CreateCampaignModalState from "./State/CreateCampaignModalState";

export interface CreateCampaignModalProps {
  isOpen: boolean;
  handleClose: () => void;
  onEdit: (isEditing: boolean) => void;
}

export default function CreateCampaignModal({ isOpen, handleClose, onEdit }: CreateCampaignModalProps) {
  const {
    getters: {
      steps,
      campaign,
      activeStep,
      selectedCandidates,
      canAdvancedToNextStep,
      isCampaignSaved,
      saveCampaignLoading,
      messages,
      candidateListLoading,
    },
    effects: {
      handleCampaignNameChange,
      handleNext,
      handleBack,
      handleSave,
      handleReset,
      handleCandidatesChange,
      handleCandidatesLoading,
    },
  } = CreateCampaignModalState();

  const editorRef = useRef<TinyMCETypes.Editor | null>(null);

  useEffect(() => {
    onEdit(
      (campaign.name && campaign.name.trim() !== "") || messages.length > 0 || selectedCandidates.length > 0,
    );
  }, [onEdit, campaign, messages, selectedCandidates]);

  const EmailStep = (
    <>
      <ScheduleEmail
        isGroup={false}
        disableAddButton={false}
        tooltipTextButton={"Tooltip text"}
        sendCount={selectedCandidates.length}
        InputEmailComponent={
          <SendEmailNow
            disableSaveAsTemplateButton={true}
            handleSelectedTemplateRow={() => console.log("selected template")}
            saveEmailTemplate={() => console.log("save template")}
            disableTemplateButton={false}
            disableTemplateMessage={"disabled"}
            InputEmailComponent={<EmailMessageInput disableInput={false} editorRef={editorRef} />}
          />
        }
      />
    </>
  );

  return (
    <Modal
      title="Create Campaign"
      dataTestId="create-campaign-modal"
      size="md"
      footerContent={
        <>
          <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
            {activeStep === steps.length ? (
              <>
                <Box sx={{ flex: "1 1 auto" }} />
                <Button
                  onClick={() => {
                    handleClose();
                    handleReset();
                  }}
                  disabled={!isCampaignSaved || saveCampaignLoading}
                >
                  Close
                </Button>
              </>
            ) : (
              <>
                <Stack direction="row" justifyContent={"end"}>
                  {candidateListLoading && <CircularProgress size={12} sx={{ mt: 1.5, mr: 2 }} />}
                  <Typography sx={{ mr: 1, mt: 0.75 }}>
                    {`${selectedCandidates.length === 0 ? "No" : selectedCandidates.length}
                    candidates selected`}
                  </Typography>
                  {activeStep !== steps.length && (
                    <Button color="inherit" disabled={activeStep === 0} onClick={handleReset} sx={{ mr: 1 }}>
                      Reset
                    </Button>
                  )}
                  <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
                    Back
                  </Button>
                  <Box sx={{ flex: "1 1 auto" }} />
                  {activeStep === steps.length - 1 ? (
                    <Button onClick={handleSave}>Save</Button>
                  ) : (
                    <Button disabled={!canAdvancedToNextStep} onClick={handleNext}>
                      Next
                    </Button>
                  )}
                </Stack>
              </>
            )}
          </Box>
        </>
      }
      content={
        <>
          <Stepper activeStep={activeStep}>
            {steps.map((label, index) => {
              const stepProps: { completed?: boolean } = {};
              const labelProps: {
                optional?: React.ReactNode;
              } = {};
              return (
                <Step key={label} {...stepProps}>
                  <StepLabel {...labelProps}>{label}</StepLabel>
                </Step>
              );
            })}
          </Stepper>
          {activeStep === steps.length ? (
            <>
              {saveCampaignLoading ? (
                <LoadingSpinner />
              ) : (
                <Box
                  sx={{
                    minHeight: 200,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="h6" sx={{ p: 2 }}>
                    All steps completed - you&apos;re finished!
                  </Typography>
                </Box>
              )}
            </>
          ) : (
            <Box sx={{ p: 1, mt: 2 }}>
              {activeStep === 0 && (
                <MessageCampaignInfoStep
                  campaignName={campaign.name}
                  onCampaignNameChange={handleCampaignNameChange}
                />
              )}
              {activeStep === 1 && (
                <CandidatesStep
                  onChange={handleCandidatesChange}
                  selectedCandidates={selectedCandidates}
                  isLoading={handleCandidatesLoading}
                />
              )}
              {activeStep === 2 && EmailStep}
              {activeStep === 3 && <ReviewStep campaign={campaign} messages={messages} />}
            </Box>
          )}
        </>
      }
      handleCloseModal={() => {
        handleClose();
        if (activeStep === steps.length) {
          handleReset();
        }
      }}
      isOpen={isOpen}
    />
  );
}
